import { cn } from '@/lib/utils'

interface ShareBarProps {
  /** 0…1. Values above 1 are clamped rather than overflowing the track. */
  share: number
  /**
   * `reference` is the pinned total's own bar: full width, in the foreground colour, so the rows
   * below have something to be a fraction *of*.
   *
   * The collector-category row is amber, because it is a warning and not a product group.
   */
  tone?: 'magnitude' | 'warning' | 'reference'
  /** The percentage beside the bar. Off where there is no room for it. */
  showValue?: boolean
  className?: string
}

const percent = new Intl.NumberFormat('de-AT', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

/**
 * The share of the total, as a bar inside a table cell (CHG-2026-09-manager-reporting).
 *
 * One hue for every row. Colouring bars darker-where-bigger would double-encode the length as hue
 * and burn the only free channel on information the bar already shows.
 *
 * **The bar is a share of the total, and the total row draws its own bar full and black**
 * (CHG-2026-09-article-location-filter). Scaling each bar to the largest row instead would fill the
 * leader's track completely while the number beside it read 41 % — two answers to the same question
 * in the same cell.
 *
 * The percentage is text next to the bar rather than a tooltip: the bar answers "roughly how much",
 * the number answers "how much", and on a printed report only one of the two survives.
 */
export function ShareBar({
  share,
  tone = 'magnitude',
  showValue = true,
  className,
}: ShareBarProps) {
  const clamped = Math.max(0, Math.min(1, share))
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Browsers drop background colours when printing, which would leave an empty outline where
          the bar is. This is one of the few places the ink is the information. */}
      <div
        className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted"
        aria-hidden="true"
        data-print-ink
      >
        <div
          className={cn(
            'h-full rounded-full',
            tone === 'warning'
              ? 'bg-warning'
              : tone === 'reference'
                ? 'bg-foreground'
                : 'bg-magnitude',
          )}
          style={{ width: `${clamped * 100}%` }}
        />
      </div>
      {showValue ? (
        <span
          className={cn(
            'w-12 shrink-0 text-right text-xs tabular-nums',
            tone === 'reference' ? 'font-medium' : 'text-muted-foreground',
          )}
        >
          {percent.format(clamped)}
        </span>
      ) : null}
    </div>
  )
}
